/**
 * Chi sta usando il pannello, e cosa gli si può mostrare.
 *
 * Una nota che vale per tutto il file: **niente qui dentro è una barriera di
 * sicurezza.** I permessi servono a decidere cosa *offrire* — quali voci di menu
 * esistono, quali pulsanti si disegnano — e ogni operazione viene comunque
 * ricontrollata dal server a ogni richiesta. Chi modificasse questo elenco dal
 * browser otterrebbe solo uno schermo pieno di rifiuti.
 *
 * Il motivo per cui esiste comunque è l'opposto di quello che sembra: senza,
 * l'unica alternativa è mostrare tutto a tutti e far scoprire i limiti a forza
 * di errori — che è precisamente ciò che faceva il sistema vecchio, dove 496
 * utenti su 505 avevano ruolo nullo e ogni chiamata autenticata era di fatto una
 * chiamata da amministratore.
 */

import { computed, ref, shallowRef, watch } from 'vue';
import { defineStore } from 'pinia';
import type { AuthUser } from '@bestehr/shared';
import { api, onSessionePersa } from '@/lib/http';
import { videochiamate } from '@/lib/videochiamate';
import { SCENA_FINTA, utenteFinto } from '@/lib/scena-finta';
import { guastoDa, type Guasto } from '@/lib/guasto';

/**
 * Le pause fra un tentativo di verifica e il successivo, in millisecondi.
 *
 * L'elenco contiene le *attese*, quindi i tentativi sono uno in più: tre in
 * tutto, poco più di un secondo e mezzo nel caso peggiore. La misura è tarata
 * sul caso vero, che non è «la rete non c'è» ma «la rete non c'era per un
 * istante»: il portatile che riprende dalla sospensione, il Wi-Fi che cambia
 * punto d'accesso proprio mentre la pagina si carica, il primo pacchetto perso
 * di una connessione che si sta risvegliando. Sono centinaia di millisecondi, e
 * chiedere una seconda volta li copre tutti.
 *
 * Più lunga non servirebbe e farebbe danno: oltre un paio di secondi non è più
 * un'esitazione, è un server irraggiungibile — e siccome l'avvio del pannello
 * attende questa verifica dietro il velo (vedi `main.ts`), insistere oltre
 * significa solo far girare più a lungo un indicatore che non porterà a niente.
 */
const PAUSE_RITENTATIVO = [400, 1_200] as const;

const attesa = (ms: number): Promise<void> => new Promise((esegui) => setTimeout(esegui, ms));

export const useAuthStore = defineStore('auth', () => {
  /**
   * `shallowRef` e non `ref`: l'utente è un oggetto che si sostituisce intero,
   * mai modificato campo per campo. Renderlo profondamente reattivo
   * significherebbe far attraversare a Vue una quarantina di proprietà a ogni
   * assegnazione per non guadagnare nulla.
   */
  const user = shallowRef<AuthUser | null>(null);

  /**
   * Se sulla sessione si sia avuta una **risposta**.
   *
   * Distinto da "non c'è utente": alla prima apertura il cookie potrebbe essere
   * valido e noi non saperlo ancora. Mandare all'ingresso in quell'istante
   * significa buttare fuori chi era dentro, ogni volta che ricarica la pagina.
   *
   * E distinto anche da "la verifica è finita", che è la sfumatura per cui
   * questo commento è stato riscritto. Vero significa che il server ha detto
   * qualcosa — «ecco chi è» oppure «questa sessione non vale più» — non che ci
   * abbiamo provato. Una richiesta che non riceve risposta non insegna nulla su
   * chi sta guardando, e segnarla come conclusa trasformerebbe un inciampo della
   * rete nella frase «non sei entrato»: l'unica delle due che ha come rimedio
   * riscrivere la password.
   */
  const pronto = ref(false);

  /**
   * Un'operazione di sessione **chiesta da chi guarda**: ingresso, uscita,
   * cambio password.
   *
   * La verifica d'avvio non sta più qui dentro, ed è il difetto che questo
   * commento nomina prima che qualcuno lo rimetta. Un indicatore che dice «sto
   * lavorando» senza dire *a che cosa* lo spegne il primo che finisce, per
   * tutti: la verifica di sfondo che concludeva mentre l'ingresso era ancora in
   * volo lo azzerava a metà accesso, e la stessa raffica lo teneva acceso quando
   * di chiesto non c'era niente. Oggi nessuna schermata lo guarda — ogni modulo
   * tiene un proprio indicatore locale — quindi il danno sarebbe arrivato il
   * giorno in cui qualcuno avesse iniziato a fidarsene, che è il momento
   * peggiore per scoprirlo. Chi ha il proprio stato lo tiene: vedi
   * `verificaAttiva`.
   */
  const inCorso = ref(false);

  /**
   * Il ciclo di verifica della sessione sta chiedendo al server.
   *
   * Separato da `inCorso` perché risponde a un'altra domanda — «il pannello si
   * sta ancora chiarendo chi sei», non «l'azione che hai chiesto è partita» — e
   * perché i due si sovrappongono per davvero: la guardia rifà la verifica a
   * ogni rotta finché `pronto` è falso, e l'ingresso è una rotta.
   *
   * Distinto anche da `verificaInCorso` più sotto, che sa la stessa cosa ma per
   * un altro scopo: quello è un `let` con dentro la promessa, e serve a non far
   * partire due raffiche insieme. Non essendo reattivo nessuna schermata lo può
   * guardare, ed è il motivo per cui non basta: il «Riprova» dell'ingresso non
   * ha modo di dire che si sta già riprovando, e un pulsante che non cambia
   * dopo la pressione lo si preme una seconda volta. Questo è il valore da
   * leggere quando lo si vorrà mostrare.
   */
  const verificaAttiva = ref(false);

  /**
   * Perché la verifica d'avvio non ha concluso. `null` quando non c'è niente da
   * dire, cioè quasi sempre.
   *
   * Vale solo per il caso in cui `pronto` è rimasto falso: la risposta non è
   * arrivata e la sessione resta ignota. È un `Guasto` intero e non una frase
   * perché la sola frase non basta a decidere se offrire «Riprova» — è il difetto
   * documentato in testa a `lib/guasto.ts`, e non c'è motivo di ripeterlo qui —
   * e perché così una schermata può mostrarlo con `BloccoErrore` senza tradurre
   * niente: `ritentabile` è vero per costruzione, e `offline` sceglie l'icona
   * giusta fra rete caduta e guasto del server.
   */
  const guastoSessione = ref<Guasto | null>(null);

  /**
   * L'insieme dei permessi, non l'elenco.
   *
   * `can()` viene chiamato da ogni voce di menu, da ogni pulsante e da ogni
   * guardia di rotta, cioè centinaia di volte per ogni disegno della pagina. Su
   * un elenco è una scansione, su un insieme è una ricerca diretta — e la
   * differenza si vede sulla barra laterale, che le valuta tutte insieme.
   */
  const permessi = computed(() => new Set(user.value?.permissions ?? []));

  /**
   * I reparti di cui questa persona è responsabile dei turni. Vuoto per quasi tutti. La barra
   * laterale lo legge per accendere «Turni», e la schermata dei turni per scoparsi ai reparti
   * giusti. Chi è responsabile lo decide l'admin, non un permesso.
   */
  const managedDepartmentIds = computed(() => user.value?.managedDepartmentIds ?? []);

  /**
   * Se questa persona ha davvero qualcuno di cui approvare le assenze (`subjectsOf` non vuoto
   * lato server). Il menu laterale lo legge per accendere «Approvazione ferie» solo a chi ha
   * sottoposti — non a un amministratore senza. Diverso da `isApprovator`, che è un flag
   * amministrativo e può essere vero anche per chi non ha nessun assegnato.
   */
  const hasSubjects = computed(() => user.value?.hasSubjects ?? false);

  /**
   * Se questa persona è un **valutatore** (D43): accende la voce «Valutazioni» nel menu e apre la
   * sua rotta. Non è un permesso del catalogo — valutare è ortogonale al ruolo — ma una proprietà
   * dell'account istituita da backend, che arriva da `/auth/me`. Chi non lo è non vede la sezione
   * e, se prova la rotta a mano, la guardia lo rimanda: il server la chiude comunque.
   */
  const canEvaluate = computed(() => user.value?.canEvaluate ?? false);

  /**
   * Se nello stabilimento di questa persona **si fanno** spedizioni e ritiri.
   *
   * È la prima delle sette abilitazioni per sede che il Cruscotto legge: fino a oggi le
   * guardava soltanto il telefono, perché erano accese dappertutto e non c'era niente da
   * nascondere a nessuno. `enableLogistics` nasce invece per dire **no** a una sede precisa
   * (Gissi, decisione di Cosimo del 2026-10-03), quindi la sezione «Logistica» della barra
   * laterale la legge.
   *
   * Il valore arriva già risolto da `/auth/me`: la regola «sulla persona `null` vuol dire
   * eredita dalla sede» vive in un posto solo, `funzioni-attive.ts` sul server, e qui arrivano
   * booleani veri. Da questa parte non si deve sapere che esistono due livelli — e soprattutto
   * non si deve riscrivere quella regola, che è il modo in cui le due metà divergono.
   *
   * Il ripiego è `false`, e il verso è scelto: finché non si sa, non si mostra. Una voce che
   * compare per mezzo secondo e poi sparisce è peggio di una voce che arriva un attimo dopo.
   */
  const enableLogistics = computed(() => user.value?.enableLogistics ?? false);

  /**
   * Se questa persona **chatta**: la stessa abilitazione che apre la chat nell'app.
   *
   * Fino al 2026-10-31 la voce «Chat» del Cruscotto guardava il solo `chat:use`, e chi aveva la
   * chat spenta sulla scheda (o dalla sede) la ritrovava qui. Adesso il server la rifiuta
   * (`@PretendeLaFunzione('enableChat')`), e la voce si spegne nello stesso caso.
   */
  const enableChat = computed(() => user.value?.enableChat ?? false);

  /**
   * Il codice del **ruolo** di chi guarda (`admin`, `hr`, `employee`…), o `null` prima che si sappia.
   *
   * Serve a una sola famiglia di voci: quelle che il server chiude **sul ruolo e non su un
   * permesso** — oggi le «Chiavi API» (`@Roles(RoleCode.ADMIN)` sul controller, [[K675]]), che
   * aprono l'anagrafica e i turni di tutta l'azienda a un sistema esterno. `settings:manage` da
   * solo non basta a dire «sysadmin»: lo dice il ruolo, e il menu deve fare la stessa domanda che
   * fa il server, o offre una porta che poi risponde 403.
   */
  const roleCode = computed<string | null>(() => user.value?.roleCode ?? null);

  const autenticato = computed(() => user.value !== null);

  /**
   * L'utente deve cambiare la password prima di poter fare altro.
   *
   * Il server lo impone comunque — ogni rotta che non sia il cambio password
   * risponde con un rifiuto — ma l'interfaccia deve *portarci*, non lasciare che
   * ci si sbatta contro.
   */
  const deveCambiarePassword = computed(() => user.value?.mustChangePassword === true);

  /** Ha **questo** permesso. */
  function can(codice: string): boolean {
    return permessi.value.has(codice);
  }

  /** Ha **almeno uno** dei permessi indicati. È la regola delle voci di menu. */
  function canAny(...codici: string[]): boolean {
    return codici.some((c) => permessi.value.has(c));
  }

  /** Ha **tutti** i permessi indicati. */
  function canAll(...codici: string[]): boolean {
    return codici.every((c) => permessi.value.has(c));
  }

  interface RispostaSessione {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }

  async function login(identifier: string, password: string): Promise<void> {
    inCorso.value = true;
    try {
      // `noRefresh`: su questa rotta un 401 è "credenziali sbagliate", non una
      // sessione scaduta da rinnovare.
      const res = await api.post<RispostaSessione>(
        '/auth/login',
        { identifier, password },
        { noRefresh: true },
      );
      user.value = res.user;
      pronto.value = true;
      // Un ingresso riuscito è la risposta più definitiva che ci sia: qualunque
      // verifica rimasta in sospeso per mancanza di rete non ha più niente da
      // raccontare, e lasciarla in vista significherebbe annunciare che il
      // server non risponde sopra una schermata appena arrivata dal server.
      guastoSessione.value = null;
    } finally {
      inCorso.value = false;
    }
  }

  /**
   * L'ingresso confermato dal telefono col QR (2026-10-28): la sessione l'ha gia' messa il server
   * nei cookie (`POST /auth/qr/stato`), qui si prende la persona come dopo `login`.
   */
  function entratoConQr(chi: AuthUser): void {
    user.value = chi;
    pronto.value = true;
    guastoSessione.value = null;
  }

  /**
   * La verifica in volo, se ce n'è una.
   *
   * Serve da quando la verifica può finire **senza concludere**: se `pronto`
   * resta falso, la guardia di navigazione la richiama alla rotta successiva —
   * e il rimando all'ingresso è già una rotta successiva. Senza questo, il primo
   * inciampo di rete produrrebbe due raffiche di tentativi sovrapposte invece di
   * una, che è il modo più rapido di trasformare mezzo secondo di rete assente
   * in sei richieste.
   *
   * È lo stesso accorgimento con cui `lib/http.ts` tiene un rinnovo solo per
   * dieci chiamate scadute insieme, e per la stessa ragione: chi arriva secondo
   * deve attendere **questa** risposta, non chiederne un'altra.
   */
  let verificaInCorso: Promise<void> | null = null;

  /**
   * Verifica la sessione all'avvio.
   *
   * Chiamata prima che la navigazione decida qualcosa. Un rifiuto per mancata
   * autenticazione **non è un errore**: è la risposta normale di chi apre il
   * pannello senza essere entrato, e trattarla come un'eccezione riempirebbe la
   * console di rosso a ogni apertura.
   *
   * Il silenzio della rete invece è tutt'altra cosa, ed è il motivo per cui
   * questa funzione non è più tre righe. Vedi `tentaVerifica`.
   */
  async function verificaSessione(): Promise<void> {
    if (pronto.value) return;
    // La scena finta di sviluppo (`lib/scena-finta.ts`): nessuna chiamata, un utente inventato.
    if (SCENA_FINTA) {
      user.value = utenteFinto();
      pronto.value = true;
      return;
    }
    verificaInCorso ??= tentaVerifica().finally(() => {
      verificaInCorso = null;
    });
    // `await` e non il semplice rinvio della promessa: chi ha chiamato deve
    // ritrovare questa funzione nella traccia dell'errore, non solo la richiesta
    // che l'ha originato.
    return await verificaInCorso;
  }

  /**
   * Chiede al server chi sta guardando, distinguendo le due risposte che non
   * sono la stessa.
   *
   * Il difetto che questa funzione ripara stava in un `finally`: `pronto`
   * diventava vero *comunque*, quindi anche quando la richiesta non aveva
   * ricevuto risposta affatto. Da lì in poi il pannello non aveva più modo di
   * sapere che non sapeva, e un mezzo secondo di rete assente durante il primo
   * caricamento — il portatile che si risveglia, l'ufficio che cambia linea —
   * diventava indistinguibile da «questa persona non è entrata». Chi aveva la
   * sessione valida in tasca si ritrovava alla schermata d'accesso a riscrivere
   * una password che non era mai stata il problema.
   *
   * Le due situazioni si separano con `guastoDa()`, che è dove questo progetto
   * tiene già la regola: `ritentabile` falso vuol dire che il server **ha
   * risposto** e la risposta non cambierà ripetendola — 401, 403, 404. È il
   * discriminante con cui `BloccoErrore` decide se disegnare «Riprova», e non
   * c'era ragione di inventarne un secondo qui che prima o poi ne divergesse.
   *
   *  - **Risposta definitiva.** La sessione è finita: si svuota e si segna
   *    `pronto`. Chi va all'ingresso ci va perché ci deve andare.
   *  - **Risposta mai arrivata.** Si richiede, poche volte e in fretta
   *    (`PAUSE_RITENTATIVO`), perché un'esitazione della rete si supera
   *    chiedendo di nuovo e non c'è nessuno meglio piazzato di qui per farlo:
   *    l'utente non ha ancora visto niente, l'avvio è dietro il velo, e un
   *    ritentativo che riesce è un difetto che non è mai esistito. Se anche
   *    l'ultimo tentativo tace, **`pronto` resta falso**: la sessione è ignota,
   *    non assente, e questa è l'unica affermazione vera disponibile.
   *
   * Che restare non-pronti non sia un blocco dipende da due cose, entrambe già
   * in piedi. La guardia in `router/index.ts` cattura questo rifiuto e prosegue
   * lo stesso, quindi si arriva all'ingresso — l'unica schermata che sappia
   * funzionare senza server — invece che a una pagina che non si disegna mai. E
   * siccome `pronto` è rimasto falso, la verifica è **riarmata**: la navigazione
   * successiva la rifà, e appena la rete torna la sessione conservata nel cookie
   * viene ritrovata da sola. L'ingresso resta comunque una via d'uscita a mano,
   * perché `login()` segna `pronto` per conto suo.
   *
   * L'utente non si azzera in questo ramo, al contrario di prima. Una richiesta
   * senza risposta non ha scoperto che non c'è nessuno: non ha scoperto niente,
   * e scrivere `null` sarebbe stato affermare l'unica cosa che non sappiamo.
   *
   * Su tutto questo vale una regola sola, ripetuta dopo **ogni** `await` del
   * ciclo: se nel frattempo `pronto` è diventato vero, qui non si scrive più
   * niente e si esce. Non è una questione di quanti tentativi restano — è che
   * questa raffica dura fino a un secondo e mezzo, e in quel tempo la sessione
   * può essersi chiarita per un'altra via. La via che conta è `login()`, che
   * segna `pronto` per conto suo, e il momento in cui accade è precisamente
   * quello in cui qualcuno ha premuto «Riprova» e poi ha digitato la password.
   */
  async function tentaVerifica(): Promise<void> {
    verificaAttiva.value = true;
    try {
      for (let tentativo = 0; ; tentativo += 1) {
        try {
          const chiSta = await api.get<AuthUser>('/auth/me', { noRefresh: false });

          // La risposta si legge in una variabile e si scrive **dopo** il
          // controllo, non direttamente in `user`: fra la partenza della
          // richiesta e il suo ritorno passano fino a un secondo e mezzo di
          // pause, e in quel tempo la sessione può essere diventata nota per
          // un'altra via — un `login()` andato a buon fine. Da lì in poi questa
          // risposta racconta un momento che non c'è più.
          if (pronto.value) return;

          user.value = chiSta;
          guastoSessione.value = null;
          pronto.value = true;
          return;
        } catch (err) {
          // Il controllo **prima** di guardare il guasto, e non solo in cima al
          // ciclo: è il caso che buttava fuori chi era appena entrato. La
          // pressione su «Riprova» lascia in volo un `/auth/me`; se l'ingresso
          // riesce mentre quella richiesta è ancora per strada, il 401 che
          // torna dopo è una risposta senza più padrone — e sotto scriveva
          // `user.value = null`, cioè la porta sbattuta in faccia nell'istante
          // esatto in cui si era entrati, senza niente sullo schermo che
          // spiegasse perché.
          if (pronto.value) return;

          const guasto = guastoDa(err, 'Non è stato possibile verificare la sessione.');

          if (!guasto.ritentabile) {
            user.value = null;
            guastoSessione.value = null;
            pronto.value = true;
            return;
          }

          // La pausa mancante *è* la fine dei tentativi: l'indice oltre il fondo
          // dell'elenco dice già tutto, e chiederlo in due modi diversi — una
          // condizione sulla lunghezza e poi la lettura — è il punto in cui i
          // due si scollano.
          const pausa = PAUSE_RITENTATIVO[tentativo];
          if (pausa === undefined) {
            guastoSessione.value = guasto;
            throw err;
          }

          await attesa(pausa);

          // E di nuovo dopo l'attesa, che è l'`await` più lungo dei due: se nel
          // frattempo si è entrati, il tentativo successivo non ha più niente
          // da chiedere. Chiederlo comunque significherebbe una richiesta in
          // più su una rete che sta già faticando, e riaprirebbe la stessa
          // finestra da cui il difetto qui sopra è entrato.
          if (pronto.value) return;
        }
      }
    } finally {
      // Qui il controllo non serve, ed è voluto: questo indicatore appartiene
      // solo a questo ciclo, e `verificaSessione` garantisce che di cicli ce ne
      // sia al massimo uno alla volta. Non c'è nessun altro proprietario a cui
      // spegnerlo sotto — che era esattamente il difetto quando la riga diceva
      // `inCorso`.
      verificaAttiva.value = false;
    }
  }

  /**
   * La rilettura in volo, se ce n'è una.
   *
   * Esiste per una sola ragione, ed è la stessa del ciclo di verifica: una
   * risposta che torna quando la domanda ha smesso di avere senso non deve
   * poter scrivere sullo stato. Qui la domanda smette di avere senso quando si
   * esce — e le due cose stanno una accanto all'altra sulla schermata del
   * profilo, «Rileggi» e «Esci». Senza annullamento, la rilettura partita un
   * istante prima dell'uscita atterra dopo e rimette dentro l'utente appena
   * uscito: barra laterale con il suo nome, menu con i suoi permessi, e nessun
   * cookie dietro a reggerli.
   */
  let ricaricaCorrente: AbortController | null = null;

  /** Quante uscite ci sono state: una scelta di posta partita prima di un'uscita non scrive. */
  let uscitePosta = 0;

  /**
   * Le copie per posta delle assenze dei propri sottoposti, scelte da sé.
   *
   * La rotta torna l'`AuthUser` intero — com'è **dopo** la scrittura — e qui si mette al posto
   * di quello in memoria: niente seconda lettura di `/auth/me`, e nessun momento in cui la
   * pagina mostra la scelta vecchia. Stessa forma della lingua sull'app.
   */
  async function scegliPosta(scelta: {
    mailRichieste?: boolean;
    mailApprovazioni?: boolean;
    mailRiepilogo?: boolean;
  }): Promise<void> {
    const giro = uscitePosta;
    const dopo = await api.patch<AuthUser>('/auth/me/posta', scelta);
    // Premuto «Esci» mentre la scelta era in volo, la risposta arrivata dopo rimetteva dentro
    // l'utente appena uscito (e la rotta d'ingresso lo rimandava al pannello, senza cookie).
    if (giro !== uscitePosta) return;
    user.value = dopo;
  }

  /** Rilegge l'utente dopo un cambiamento (profilo, permessi, cambio password). */
  async function ricarica(): Promise<void> {
    // La precedente si annulla: fra due riletture vince l'ultima chiesta, non
    // l'ultima arrivata — che sono la stessa cosa solo finché la rete si
    // comporta bene.
    ricaricaCorrente?.abort();
    const mio = (ricaricaCorrente = new AbortController());

    try {
      const chiSta = await api.get<AuthUser>('/auth/me', { signal: mio.signal });
      if (mio.signal.aborted) return;
      user.value = chiSta;
    } catch (err) {
      // Un annullamento non è un guasto e non va raccontato a nessuno: la
      // schermata del profilo mostrerebbe «Non è stato possibile rileggere il
      // profilo» per una rilettura che abbiamo interrotto noi.
      if (mio.signal.aborted) return;
      throw err;
    } finally {
      // Solo se è ancora la nostra: se una rilettura più recente ha già preso
      // il posto, azzerare qui vorrebbe dire perderne il riferimento e non
      // poterla più annullare all'uscita.
      if (ricaricaCorrente === mio) ricaricaCorrente = null;
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    inCorso.value = true;
    try {
      /*
        Senza `noRefresh` (rinnovoPrimaDelCambio). La password attuale sbagliata è un 400 con
        `campi.currentPassword`, non un 401: qui un 401 vuol dire soltanto che il gettone d'accesso
        (`JWT_ACCESS_TTL`, minuti e non l'anno del rinnovo) è scaduto mentre si compilava il modulo. Con `noRefresh` quel 401 buttava
        fuori — «sessione scaduta», password non cambiata, da rientrare — chi aveva il rinnovo
        valido per un anno: una sessione chiusa per un'euristica, contro il login perpetuo. Il
        rinnovo e la ripetuta li fa `lib/http.ts`; se il rinnovo fallisce davvero, l'uscita resta.
      */
      const res = await api.post<RispostaSessione>('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      user.value = res.user;
    } finally {
      inCorso.value = false;
    }
  }

  async function logout(): Promise<void> {
    inCorso.value = true;
    try {
      // `/auth/refresh/logout` e non `/auth/logout`: solo li' arriva il cookie del rinnovo, e il
      // server chiude la sessione di questo browser e non quella del telefono (2026-10-28).
      await api.post('/auth/refresh/logout');
    } catch {
      // L'uscita non fallisce mai dal punto di vista di chi la chiede: se il
      // server non risponde, i cookie scadranno da soli e qui dentro non resta
      // niente. Trattenere l'utente dentro un pannello che crede vuoto sarebbe
      // il comportamento peggiore dei due.
    } finally {
      // `dimentica()` e non `user.value = null` scritto una seconda volta: le
      // due uscite — questa e quella annunciata da `lib/http.ts` — devono
      // lasciare il deposito nello stesso stato, e il modo per esserne certi è
      // che sia la stessa riga. Quando all'uscita si è aggiunto l'annullamento
      // della rilettura, due copie avrebbero significato una sola aggiornata.
      dimentica();
      inCorso.value = false;
    }
  }

  /** Svuota tutto senza chiamare il server: la sessione è già persa. */
  function dimentica(): void {
    // Prima l'annullamento, poi lo svuotamento: una rilettura ancora in volo
    // risponderebbe fra un istante con l'utente di prima, e riscriverebbe
    // sopra il `null` qui sotto. Chi è uscito resterebbe dentro per finta.
    ricaricaCorrente?.abort();
    uscitePosta += 1;
    user.value = null;
  }

  /**
   * Se il server delle videochiamate c'è (`GET /videochiamate/disponibili`, 2026-10-26).
   *
   * Sta qui e non in una pagina perché lo chiede il **menu**: la voce «Videochiamate» si accende
   * solo se il motore video esiste, e il menu legge il deposito di chi guarda. Si legge una volta
   * per sessione, quando si sa chi sta guardando; `false` finché non si sa, così la voce non
   * compare e sparisce. Un errore di lettura vale `false`: una voce che porta a una stanza che
   * non si apre è peggio di una voce che manca.
   */
  const videochiamateDisponibili = ref(SCENA_FINTA);

  watch(
    () => user.value?.id ?? null,
    async (id) => {
      if (id === null) {
        videochiamateDisponibili.value = false;
        return;
      }
      if (SCENA_FINTA) {
        videochiamateDisponibili.value = true;
        return;
      }
      try {
        const d = await videochiamate.disponibili();
        videochiamateDisponibili.value = d.disponibili === true;
      } catch {
        videochiamateDisponibili.value = false;
      }
    },
    { immediate: true },
  );

  onSessionePersa(dimentica);

  return {
    videochiamateDisponibili,
    user,
    pronto,
    inCorso,
    verificaAttiva,
    guastoSessione,
    autenticato,
    deveCambiarePassword,
    permessi,
    managedDepartmentIds,
    hasSubjects,
    canEvaluate,
    enableLogistics,
    enableChat,
    roleCode,
    can,
    canAny,
    canAll,
    login,
    entratoConQr,
    logout,
    dimentica,
    ricarica,
    scegliPosta,
    verificaSessione,
    changePassword,
  };
});
